import React, { FC, useEffect, useState } from "react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import GoogleMaps from "../GoogleMaps/GoogleMaps";
import { z } from "zod";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { handleSendForm } from "../../../src/API/ContactService/contactService";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";

interface ContactFormProps {}

const schema = z.object({
  email: z.string().email("Adresse e-mail invalide"),
  company: z.string().min(1, "Société est obligatoire"),
  lastName: z.string().min(1, "Le nom est obligatoire"),
  firstName: z.string().min(1, "Le prénom est obligatoire"),
  city: z.string().min(1, "Ville est est obligatoire"),
  phone: z.string().min(1, "Numéro téléphone est obligatoire"),
  receiver: z.string().min(1, "Destinatire est obligatoire"),
  subject: z.string().min(1, "Sujet est obligatoire"),
  message: z.string().min(1, "message est obligatoire"),
});

const ContactForm: FC<ContactFormProps> = ({}) => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema) });

  const onSubmit = async (data: any) => {
    const response = await handleSendForm(data, schema, navigate);
    if (response === "ok") {
      reset();
    }
  };

  return (
    <div>
      <div
        className={`container overflow-hidden p-10 `}
        data-nc-id="PageContact"
      >
        <div className="mb-24 lg:mb-20">
          <h5
            className="my-12 sm:my-18 flex items-starts text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-start"
            style={{ fontSize: "16px", fontWeight: "100", color: "#a3a3a3" }}
          >
            Nous contacter
          </h5>

          <div className="flex flex-wrap ">
            {/* Bannières de contact à gauche */}
            <div className="pl-4 pr-4 sm:w-1/2 md:w-1/2 ">
              <div className="space-y-6">
                {/* Bannière AQUAVESC */}
                <Card className="bg-secondary hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-secondary/50 group">
                  <CardContent className="p-6">
                    <div className="flex flex-col gap-4">
                      <h3 className="text-xl font-bold text-white">Contactez AQUAVESC</h3>
                      <p className="text-white/90">
                        Pour toute question concernant l'eau potable et les services AQUAVESC
                      </p>
                      <a
                        href={process.env.REACT_APP_AQUAVESC_CONTACT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-white text-secondary hover:bg-gray-100 font-semibold px-4 py-2 rounded-md group-hover:translate-x-2 transition-all duration-300 w-fit cursor-pointer"
                        onClick={(e) => {
                          console.log('AQUAVESC Contact URL:', process.env.REACT_APP_AQUAVESC_CONTACT_URL);
                          if (!process.env.REACT_APP_AQUAVESC_CONTACT_URL) {
                            e.preventDefault();
                            alert('URL de contact non définie');
                          }
                        }}
                      >
                        <span>Nous contacter</span>
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                          />
                        </svg>
                      </a>
                    </div>
                  </CardContent>
                </Card>

                {/* Bannière HYDREAULYS */}
                <Card className="bg-primary hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-primary/50 group">
                  <CardContent className="p-6">
                    <div className="flex flex-col gap-4">
                      <h3 className="text-xl font-bold text-white">Contactez HYDREAULYS</h3>
                      <p className="text-white/90">
                        Pour toute question concernant l'assainissement et les services HYDREAULYS
                      </p>
                      <a
                        href={process.env.REACT_APP_HYDREAULYS_CONTACT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-white text-primary hover:bg-gray-100 font-semibold px-4 py-2 rounded-md group-hover:translate-x-2 transition-all duration-300 w-fit cursor-pointer"
                        onClick={(e) => {
                          console.log('HYDREAULYS Contact URL:', process.env.REACT_APP_HYDREAULYS_CONTACT_URL);
                          if (!process.env.REACT_APP_HYDREAULYS_CONTACT_URL) {
                            e.preventDefault();
                            alert('URL de contact non définie');
                          }
                        }}
                      >
                        <span>Nous contacter</span>
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                          />
                        </svg>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Cartes à droite (conservées) */}
            <div className="pl-4 pr-4 sm:w-1/2 md:w-1/2">
              <div className="flex flex-col h-full gap-7">
                {/* First card group */}
                <div className="flex-1">
                  <Card className="flex flex-col h-full bg-primary ">
                    <CardHeader className="flex-shrink-0"></CardHeader>
                    <CardContent className="flex items-center flex-grow">
                      <p className="font-semibold text-white">
                        Consultez le dernier{" "}
                        <a
                          href={
                            process.env
                              .REACT_APP_HYDREAULYS_MEDIA_ACTIVITES_URL
                          }
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 font-bold hover:text-secondary transition-all duration-300"
                        >
                          rapport d'activité
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>{" "}
                        d'HYDREAULYS,{" "}
                        <a
                          href={
                            process.env.REACT_APP_HYDREAULYS_MEDIA_STATUTS_URL
                          }
                          target="_blank"
                          className="inline-flex items-center gap-1 font-bold hover:text-secondary transition-all duration-300"
                          rel="noreferrer"
                        >
                          les statuts
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>{" "}
                        et derniers{" "}
                        <a
                          href={
                            process.env.REACT_APP_HYDREAULYS_MEDIA_ACTS_URL
                          }
                          target="_blank"
                          className="inline-flex items-center gap-1 font-bold hover:text-secondary transition-all duration-300"
                          rel="noreferrer"
                        >
                          actes administratifs
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>{" "}
                        .
                      </p>
                    </CardContent>
                    <CardFooter className="flex-shrink-0"></CardFooter>
                  </Card>
                </div>

                {/* Second card group */}
                <div className="flex-1">
                  <Card className="flex flex-col h-full bg-secondary ">
                    <CardHeader className="flex-shrink-0"></CardHeader>
                    <CardContent className="flex items-center flex-grow">
                      <p className="font-semibold text-white">
                        Consultez le dernier{" "}
                        <a
                          href={
                            process.env.REACT_APP_AQUAVESC_MEDIA_ACTIVITES_URL
                          }
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 font-bold hover:text-primary transition-all duration-300"
                        >
                          rapport d'activité
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>{" "}
                        d'AQUAVESC,{" "}
                        <a
                          href={
                            process.env.REACT_APP_AQUAVESC_MEDIA_STATUTS_URL
                          }
                          target="_blank"
                          className="inline-flex items-center gap-1 font-bold hover:text-primary transition-all duration-300"
                          rel="noreferrer"
                        >
                          les statuts
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>{" "}
                        et derniers{" "}
                        <a
                          href={process.env.REACT_APP_AQUAVESC_MEDIA_ACTS_URL}
                          target="_blank"
                          className="inline-flex items-center gap-1 font-bold hover:text-primary transition-all duration-300"
                          rel="noreferrer"
                        >
                          actes administratifs
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>{" "}
                        .
                      </p>
                    </CardContent>
                    <CardFooter className="flex-shrink-0"></CardFooter>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
